import * as THREE from 'three';
import { ERM_LAMP, ERM_MIRROR, ERM_SPLIT, VEL_BRAZIER, hexToWorld, velBands, velLampReady, type BattleState } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** A clay lamp, its flame. */
const LAMP = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 10.5h11c-.6 1.9-2.7 3-5.5 3s-4.9-1.1-5.5-3z" fill="currentColor"/><path d="M8 2c1.6 1.8 2.3 3.1 1.5 4.7.1-.9-.5-1.4-1-1.8-.1 1.2-1.5 1.6-1.5 2.8-.8-1.5-.3-3.2 1-5.7z" fill="currentColor"/><path d="M8 8.5v2" stroke="currentColor" stroke-width="1.2"/></svg>';
/** The altar, a lit line over it. */
const ALTAR = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 8.5h11v2h-11zM4 10.5h8v4H4z" fill="currentColor"/><path d="M8 1.5v4.5M5.5 3.5 8 6l2.5-2.5" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';
/** A candle on a stone. */
const CANDLE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6.4 7h3.2v5.5H6.4z" fill="currentColor"/><path d="M8 1.6c1.2 1.4 1.6 2.4 1 3.6-.6.9-1.4.9-2 0-.6-1.2-.2-2.2 1-3.6z" fill="currentColor"/><path d="M3.5 13.5h9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
/** Her wail: a mouth and its waves. */
const WAIL = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="5" cy="8" rx="2" ry="3" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M9 5.2c1 .8 1 4.8 0 5.6M11.2 3.6c1.8 1.4 1.8 7.4 0 8.8M13.4 2.2c2.4 2 2.4 9.6 0 11.6" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>';
/** The first light: a sun through a hole. */
const SUN = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="3" fill="currentColor"/><path d="M8 1v2.2M8 12.8V15M1 8h2.2M12.8 8H15M3 3l1.6 1.6M11.4 11.4 13 13M13 3l-1.6 1.6M4.6 11.4 3 13" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';
/** The Doliente: a tall veiled figure, the veil trailing. */
const VEIL = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="3.2" r="1.8" fill="currentColor"/><path d="M5 5.5c0 3-1 6.5-2.5 9h6c-.4-2.8-.4-6.2 0-9zM9 6.2c2 2.4 3.6 5.6 5 8.3h-3.4c-.6-2.6-1-5.2-1.6-8.3z" fill="currentColor"/></svg>';
/** A door, barred. */
const DOOR = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 14.5V4a4.5 4.5 0 0 1 9 0v10.5z" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M2 8.5 14 6.5M2 11.5l12-2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
/** The procession: three hooded heads. */
const BAND = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 14c0-3 1-5 2.5-5S7 11 7 14zM6.5 14c0-3.4 1-5.5 2.5-5.5S11.5 10.6 11.5 14zM10.5 14c0-3 1-5 2.5-5s2 2 2 5z" fill="currentColor"/><circle cx="4.5" cy="6.6" r="1.6" fill="currentColor"/><circle cx="9" cy="6" r="1.7" fill="currentColor"/><circle cx="13" cy="6.6" r="1.6" fill="currentColor"/></svg>';
/** A brazier. */
const BRAZIER = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10l-1.5 3h-7z" fill="currentColor"/><path d="M8 11v3.5M5 14.5h6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M8 1.8c1.6 1.8 2 3.4.6 5-.4-1-.9-1.3-1.4-1.6-.2 1-1 1.3-1.2 1.8-.9-1.6-.1-3.2 2-5.2z" fill="currentColor"/></svg>';
/** A mirror on its column. */
const MIRROR = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="6.5" rx="4.6" ry="4.6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M5.6 5.2l3-2.2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><path d="M8 11.1V14.5M5.5 14.5h5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';

type Mark = { el: HTMLButtonElement; at: number; point: THREE.Vector3; state: HTMLElement };

const secs = (ticks: number): number => Math.max(0, Math.ceil(ticks / 12));
const clock = (ticks: number): string => {
  const s = secs(ticks);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * The cleric's last road on screen, chapter 5 (docs/sendas/clerigo.md, «La vigilia»), inside a path's own plate and over
 * the board (the mirrors again are chapter 4's nave: game/ermita-hud.ts):
 *
 * - the lamps: how many are lit of six and which is free next; how many are out (two at once lose). Over the board, a
 *   mark on each lamp not lit (its number, the altar's its own; «in n s» for the next to come).
 * - the atrium: the nave's door (its blows left, red from half), the procession (its band, the next, the last) and who of
 *   Joran's lot is down to be got up.
 * - the Doliente: the candle (lit; a tongue at it and how long it holds; shrunk after a wail not cut); her wail (warned,
 *   in, quiet; from the sun, without pause outside it) and her embrace (the veil opening: away); the first light (in how
 *   long; the sun on the way, or not reaching). Over the board, the mirrors and dividers (to set the way again if a limpet
 *   or the frost broke it) and the brazier while a mirror is frosted.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class VigiliaHud {
  readonly panel: HTMLElement;
  private kind: 'lamps' | 'procession' | 'mourn';
  private v = new THREE.Vector3();
  private rowA = h('div.bh-row.al-waverow.vg-a');
  private textA = h('span.al-wave');
  private rowB = h('div.bh-row.al-waverow.vg-b');
  private textB = h('span.al-wave');
  private rowC = h('div.bh-row.al-waverow.vg-c');
  private textC = h('span.al-wave');
  private marks: Mark[] = [];
  private key = '';

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const H = s.rules?.senda?.litter?.hermit;
    const V = H?.vela;
    this.kind = V?.kind ?? 'lamps';
    const rows: HTMLElement[] = [];
    if (V?.kind === 'lamps') {
      this.rowA.append(h('span.bh-ico', { html: LAMP }), h('span.bh-label', {}, t('senda.camino.lamps')), this.textA);
      rows.push(this.rowA);
      const last = V.lamps.length - 1;
      V.lamps.forEach((l, i) => this.addMark(ERM_LAMP + i, l, i === last ? ALTAR : LAMP, i === last ? t('senda.camino.lampAltar') : t('senda.camino.lampMark', { n: i + 1 }), t('senda.camino.lampHint'), 1.4, i === last ? '' : String(i + 1)));
    } else if (V?.kind === 'procession') {
      this.rowA.append(h('span.bh-ico', { html: DOOR }), h('span.bh-label', {}, t('senda.camino.naveDoor')), this.textA);
      this.rowB.append(h('span.bh-ico', { html: BAND }), h('span.bh-label', {}, t('senda.camino.procession')), this.textB);
      rows.push(this.rowA, this.rowB, this.rowC);
      this.rowC.append(h('span.bh-ico', { html: BAND }), this.textC);
    } else if (V?.kind === 'mourn' && H) {
      this.rowA.append(h('span.bh-ico', { html: CANDLE }), h('span.bh-label', {}, t('senda.camino.candle')), this.textA);
      this.rowB.append(h('span.bh-ico', { html: WAIL }), h('span.bh-label', {}, t('senda.camino.wail')), this.textB);
      this.rowC.append(h('span.bh-ico', { html: SUN }), h('span.bh-label', {}, t('senda.camino.sunRow')), this.textC);
      rows.push(this.rowA, this.rowB, this.rowC);
      H.mirrors.forEach((m, i) => this.addMark(ERM_MIRROR + i, hexToWorld(m.q, m.r), MIRROR, `${t('senda.camino.mirror')} ${i + 1}`, t('senda.camino.mirrorHint'), 2.6, String(i + 1)));
      H.splits.forEach((m, i) => this.addMark(ERM_SPLIT + i, hexToWorld(m.q, m.r), MIRROR, `${t('senda.camino.split')} ${'AB'[i]}`, t('senda.camino.splitHint'), 2.6, 'AB'[i] as string));
      if (V.mourn) this.addMark(VEL_BRAZIER, V.mourn.frost.brazier, BRAZIER, t('senda.camino.brazierMark'), t('senda.camino.brazierHint'), 1.2);
    }
    void VEIL;
    this.panel = h(`div.bh.nv-panel.er-panel.vg-panel.vg-${this.kind}`, {}, ...rows);
  }

  private addMark(at: number, p: { x: number; y: number }, ico: string, name: string, hint: string, lift: number, num = ''): void {
    const icon = num ? h('span.cm-ico.nv-num', {}, num) : h('span.cm-ico', { html: ico });
    const state = h('b.nv-state');
    const cls = at === VEL_BRAZIER ? '.vg-brazier' : at >= ERM_SPLIT && at < ERM_SPLIT + 2 ? '.er-split' : at >= ERM_MIRROR && at < ERM_MIRROR + 6 ? '.nv-mirror' : num ? '.er-lamp' : '.er-lamp.er-altar';
    const el = h(
      `button.al-stone.cm-mark.nv-mark${cls}`,
      { type: 'button', 'data-test': `vigilia-mark-${at}`, 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.mark(at)) },
      h('span.al-ring'),
      icon,
      h('b.mn-name.cm-name', {}, name),
      state,
    ) as HTMLButtonElement;
    this.board.append(el);
    this.marks.push({ el, at, point: simToWorld(p.x, p.y, new THREE.Vector3(), lift), state });
  }

  update(s: BattleState): void {
    const r = s.rules?.senda?.litter;
    const w = s.senda?.litter;
    const H = r?.hermit;
    const V = H?.vela;
    const m = w?.erm;
    const v = m?.vel;
    if (!r || !w || !H || !V || !m || !v) return;
    const set = (el: HTMLElement, text: string): void => {
      if (el.textContent !== text) el.textContent = text;
    };
    if (V.kind === 'lamps' && V.lamp) {
      const lit = v.lamp.filter((x) => x === 1).length;
      const out = v.lamp.filter((x) => x === 2).length;
      const next = v.lamp.findIndex((x, i) => x !== 1 && velLampReady(V, i, s.waveT));
      const soon = v.lamp.findIndex((_, i) => !velLampReady(V, i, s.waveT));
      const lampT = out ? t('senda.camino.lampsOut', { n: out }) : next >= 0 ? (next === V.lamps.length - 1 ? t('senda.camino.lampReadyAltar') : t('senda.camino.lampReady', { n: next + 1 })) : soon >= 0 ? t('senda.camino.lampNext', { n: secs(V.lamp.every * soon - s.waveT) }) : '';
      set(this.textA, [t('senda.camino.lampsOf', { n: lit }), lampT].filter(Boolean).join(' · '));
      this.rowA.classList.toggle('warn', out > 0 || next >= 0);
    } else if (V.kind === 'procession' && V.procession) {
      const P = V.procession;
      set(this.textA, t('senda.camino.doorOf', { n: v.door, of: P.doorHits }));
      this.rowA.classList.toggle('warn', v.door * 2 <= P.doorHits);
      const all = velBands(P);
      const left = P.band.until >= s.waveT ? v.bandAt - s.waveT : -1;
      set(this.textB, left < 0 ? t('senda.camino.bandLast') : [t('senda.camino.bandOf', { n: Math.max(1, v.band), of: all }), t('senda.camino.bandNext', { n: secs(left) })].join(' · '));
      const down = (s.senda?.npcs ?? []).find((n) => (n.id === 'joran' || n.id === 'apprenticeA' || n.id === 'apprenticeB') && n.hp <= 0);
      set(this.textC, down ? t('senda.camino.guardDown', { name: t(`senda.cleric.npc.${down.id}`) }) : t('senda.camino.guardsUp'));
      this.rowC.classList.toggle('warn', !!down);
    } else if (V.kind === 'mourn' && V.mourn) {
      const M = V.mourn;
      const atC = v.tongues.filter((x) => x.at >= 0);
      const held = atC.find((x) => x.at > s.tick);
      const worst = atC.filter((x) => x.at <= s.tick).sort((a, b) => a.at - b.at)[0];
      const need = worst ? (worst.fast ? M.tongue.fast : M.tongue.snuff) : 0;
      set(
        this.textA,
        v.candle === 0
          ? t('senda.camino.candleOut')
          : worst
            ? t('senda.camino.candleTongue', { n: secs(need - (s.tick - worst.at)) })
            : held
              ? t('senda.camino.candleHeld')
              : m.fast > 0
                ? t('senda.camino.candleSmall')
                : t('senda.camino.candleLit'),
      );
      this.rowA.classList.toggle('warn', !!worst || m.fast > 0 || v.candle === 0);
      this.rowA.classList.toggle('good', !worst && !held && m.fast === 0 && v.candle === 1);
      const boss = m.boss ? s.enemies.find((e) => e.id === m.boss && e.hp > 0) : undefined;
      const on = s.waveT < m.wailUntil;
      const warn = !on && !!boss && m.wail - s.waveT <= M.wail.warn;
      const quiet = s.waveT < m.mute;
      const hug = v.hug >= 0;
      set(
        this.textB,
        !boss
          ? '—'
          : hug
            ? t('senda.camino.hug')
            : on
              ? v.phase === 2
                ? t('senda.camino.wailHunt')
                : t('senda.camino.wailOn')
              : quiet
                ? t('senda.camino.wailMute', { n: secs(m.mute - s.waveT) })
                : warn
                  ? t('senda.camino.wailWarn', { n: secs(m.wail - s.waveT) })
                  : t('senda.camino.wailNext', { n: secs(m.wail - s.waveT) }),
      );
      this.rowB.classList.toggle('warn', on || warn || hug);
      this.rowB.classList.toggle('good', quiet && !hug);
      set(this.textC, v.frost >= 0 ? t('senda.camino.frostRow') : v.sun < 0 ? t('senda.camino.sunIn', { t: clock(M.sun - s.waveT) }) : v.sun === 1 ? t('senda.camino.sunOn') : t('senda.camino.sunOff'));
      this.rowC.classList.toggle('warn', v.frost >= 0 || v.sun === 0);
      this.rowC.classList.toggle('good', v.sun === 1 && v.frost < 0);
    }
    // The marks.
    const readyKey = V.lamp ? v.lamp.map((_, i) => (velLampReady(V, i, s.waveT) ? 1 : 0)).join('') : '';
    const k = `${v.lamp.join('')}|${readyKey}|${v.frost}|${m.covered}|${m.altar}${m.door}|${s.result}|${V.lamp ? Math.floor(s.waveT / 12) : ''}`;
    if (k !== this.key) {
      this.key = k;
      for (const mk of this.marks) {
        let gone = s.result !== 'running';
        let text = '';
        if (V.lamp && mk.at >= ERM_LAMP && mk.at < ERM_LAMP + 6) {
          const i = mk.at - ERM_LAMP;
          gone ||= v.lamp[i] === 1;
          const ready = velLampReady(V, i, s.waveT);
          mk.el.classList.toggle('wait', !ready);
          mk.el.classList.toggle('out', v.lamp[i] === 2);
          const first = v.lamp.findIndex((_, j) => !velLampReady(V, j, s.waveT));
          if (!ready && i !== first) gone = true;
          text = v.lamp[i] === 2 ? t('senda.camino.lampOutMark') : ready ? '' : t('senda.camino.lampWait', { n: secs(V.lamp.every * i - s.waveT) });
        } else if (mk.at === VEL_BRAZIER) {
          gone ||= v.frost < 0;
        } else if (mk.at >= ERM_MIRROR && mk.at < ERM_MIRROR + 6) {
          const i = mk.at - ERM_MIRROR;
          const cov = ((m.covered >> i) & 1) === 1;
          const frost = v.frost === i;
          mk.el.classList.toggle('covered', cov || frost);
          text = frost ? t('senda.camino.frosted') : cov ? t('senda.camino.covered') : '';
          // (Set right, the way's marks go: a phone's board stays clean; broken, they come back.)
          gone ||= m.altar && m.door && !cov && !frost;
        } else if (mk.at >= ERM_SPLIT && mk.at < ERM_SPLIT + 2) gone ||= m.altar && m.door;
        if (mk.state.textContent !== text) mk.state.textContent = text;
        mk.el.classList.toggle('gone', gone);
        mk.el.disabled = s.result !== 'running';
        remeasureMark(mk.el);
      }
    }
    for (const mk of this.marks) {
      const sent = w.job === mk.at;
      mk.el.classList.toggle('sent', sent);
      const need = mk.at === VEL_BRAZIER ? (V.mourn?.frost.thaw ?? 36) : V.lamp && mk.at < ERM_MIRROR ? V.lamp.light : H.stand;
      mk.el.style.setProperty('--k', sent ? Math.min(1, Math.max(0, w.held) / Math.max(1, need)).toFixed(2) : '0');
    }
  }

  /** Every frame: the marks over their places. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
  }
}
